import { Stethoscope, Smile, Sparkles, type LucideIcon } from "lucide-react";

export type ClinicaVertical = {
  slug: "medicas" | "odontologicas" | "esteticas";
  name: string;
  shortName: string;
  icon: LucideIcon;
  tagline: string;
  pains: string[];
  wins: string[];
  metrics: { value: string; label: string }[];
  testimonial: { quote: string; author: string; role: string };
};

export const CLINICAS_VERTICALS: ClinicaVertical[] = [
  {
    slug: "medicas",
    name: "Clínicas Médicas",
    shortName: "Médicas",
    icon: Stethoscope,
    tagline: "Acabe com o no-show, organize a agenda e responda no WhatsApp em segundos.",
    pains: [
      "Recepção sobrecarregada com WhatsApp, telefone e Instagram",
      "Alta taxa de faltas em consultas e exames",
      "Dúvidas repetitivas sobre convênios, preparos e horários",
      "Leads chegando fora do expediente e esfriando até segunda-feira",
    ],
    wins: [
      "Confirmação e lembrete automático de consulta",
      "Pré-triagem e orientação de preparo de exames por IA",
      "Roteamento por especialidade e convênio",
      "Painel único com histórico do paciente",
    ],
    metrics: [
      { value: "-40%", label: "de faltas (no-show)" },
      { value: "+30%", label: "de agendamentos via WhatsApp" },
      { value: "38%", label: "dos leads chegam fora do horário" },
    ],
    testimonial: {
      quote:
        "O índice de faltas despencou depois que os lembretes automáticos começaram a ser enviados. E o melhor: sem esforço da equipe.",
      author: "Dr. Rafael Toledo",
      role: "Clínico geral — Clínica VivaMais",
    },
  },
  {
    slug: "odontologicas",
    name: "Clínicas Odontológicas",
    shortName: "Odontológicas",
    icon: Smile,
    tagline: "Menos faltas, mais orçamentos fechados e agenda cheia até fora do expediente.",
    pains: [
      "Orçamentos travados sem retorno do paciente",
      "Desmarques de última hora deixando cadeira ociosa",
      "Dúvidas de preço, planos e parcelamento monopolizando o WhatsApp",
      "Follow-up manual de tratamentos longos (implante, ortodontia)",
    ],
    wins: [
      "IA responde valores, planos e parcelamento 24h",
      "Lembretes com link de pré-pagamento para reduzir desistência",
      "Follow-up automático por etapa de tratamento",
      "CRM separa curiosos de quem está pronto para fechar",
    ],
    metrics: [
      { value: "-48%", label: "de faltas em consultas" },
      { value: "+22%", label: "de conversão em orçamentos" },
      { value: "+36%", label: "de agendamentos confirmados" },
    ],
    testimonial: {
      quote:
        "Conseguimos receber pagamentos via link antes da consulta. Isso reduziu drasticamente as desistências de última hora.",
      author: "Renata Carvalho",
      role: "Gerente financeira — Clínica Sorrir Mais",
    },
  },
  {
    slug: "esteticas",
    name: "Clínicas de Estética",
    shortName: "Estéticas",
    icon: Sparkles,
    tagline: "Transforme curiosos do Instagram em avaliações agendadas com o pacote certo.",
    pains: [
      "Direct do Instagram lotado sem tempo de qualificar cada lead",
      "Perguntas sobre preço de protocolos que ninguém quer responder por escrito",
      "Muita avaliação marcada, poucas fechando o pacote",
      "Retorno e sessões continuadas ficam esquecidas na agenda",
    ],
    wins: [
      "IA qualifica interesse por procedimento (botox, harmonização, laser, corporal)",
      "Encaminha pacote/valor conforme perfil e agenda avaliação",
      "Recall automático de sessões e retornos do pacote",
      "Campanhas segmentadas para reativar clientes inativos",
    ],
    metrics: [
      { value: "+45%", label: "de avaliações agendadas via Instagram" },
      { value: "+28%", label: "de conversão em pacotes fechados" },
      { value: "-55%", label: "no tempo de resposta ao lead" },
    ],
    testimonial: {
      quote:
        "A IA da Kentro filtra quem só quer preço de quem realmente quer marcar. Minha equipe só entra quando o lead está quente.",
      author: "Marina Prado",
      role: "Sócia — Clínica Estética Aura",
    },
  },
];

export const CLINICAS_COMMON_PROBLEMS = [
  {
    title: "Recepção que não dá conta",
    desc: "WhatsApp, Instagram, telefone e balcão chegando ao mesmo tempo — e paciente esperando resposta.",
  },
  {
    title: "Agenda com buracos e faltas",
    desc: "Cadeira e consultório parados custam caro. Faltas sem confirmação drenam o faturamento.",
  },
  {
    title: "Lead que esfria no fim de semana",
    desc: "38% dos contatos chegam fora do horário. Sem atendimento imediato, viram concorrente.",
  },
  {
    title: "Orçamento enviado, silêncio",
    desc: "Sem follow-up estruturado, orçamentos de tratamentos e pacotes ficam parados.",
  },
];

export const CLINICAS_FEATURES = [
  {
    title: "Atendimento omnichannel",
    desc: "WhatsApp Oficial, Instagram, telefone, e-mail e webchat em um único painel para a recepção.",
  },
  {
    title: "IA Dr. treinada para clínicas",
    desc: "Responde dúvidas de preparo, convênio, preço e agenda consultas direto no CRM — 24h.",
  },
  {
    title: "Confirmação e recall automáticos",
    desc: "Lembretes de consulta, retorno de sessão e reagendamento sem depender da equipe.",
  },
  {
    title: "CRM com prontuário de contato",
    desc: "Histórico completo de conversas, especialidade, convênio e tags por procedimento.",
  },
  {
    title: "Pré-pagamento por link",
    desc: "Reduz desistência enviando link de sinal ou pacote antes da consulta.",
  },
  {
    title: "Relatórios de produtividade",
    desc: "Tempo médio, no-show, canais e conversão por profissional — em tempo real.",
  },
];

export const CLINICAS_FAQ = [
  {
    q: "A Kentro serve para clínica pequena, com só 1 recepcionista?",
    a: "Sim. A IA absorve dúvidas repetitivas e libera a recepção para focar em quem já chegou. Muitas clínicas com 1 pessoa passam a operar como se tivessem 3.",
  },
  {
    q: "Funciona com meu sistema de agenda atual (iClinic, Dental Office, Simples Dental etc.)?",
    a: "Sim. Temos integração via API e webhook para os principais softwares de clínica. Onde não há integração nativa, o CRM da Kentro funciona como camada de agendamento e passa o dado adiante.",
  },
  {
    q: "O paciente percebe que está falando com uma IA?",
    a: "A IA Dr. é transparente e educada. Ela se apresenta como assistente virtual, tira dúvidas com tom humano e transfere para atendente humano sempre que o caso exige — sem fricção.",
  },
  {
    q: "Precisa de WhatsApp Oficial (Business API)?",
    a: "Sim, e a Kentro cuida de todo o processo de homologação do número na Meta. Isso garante que sua clínica não seja bloqueada e possa disparar lembretes em escala.",
  },
  {
    q: "Quanto tempo leva para colocar no ar?",
    a: "Do onboarding à IA respondendo em produção, em média 7 a 15 dias — dependendo do volume de conteúdo do seu segmento (protocolos, convênios, preparos).",
  },
];
